// Per-school copies of menu visibility, the admin-panel permissions matrix,
// and security controls. The district documents stay the default. A School
// Admin edits their school's copy only when a district admin has checked
// schoolSystemSettings on their user record.

export const SCHOOL_SYSTEM_SETTINGS_FIELD = 'schoolSystemSettings'

export const SCHOOL_SYSTEM_SETTING_FIELDS = [
  'navVisibility',
  'adminPanelPermissions',
  'securitySettings'
]

// Pages a school copy must not grant. Backup and bulk delete stay district-only.
// permissions / security / backup-restore are already locked in the matrix UI.
export const SCHOOL_MATRIX_LOCKED_PAGES = [
  'permissions',
  'security',
  'backup-restore',
  'delete-all-users',
  'delete-all-students'
]

export const SECURITY_OVERRIDE_KEYS = [
  'sessionTimeoutEnabled',
  'sessionTimeoutMinutes',
  'privacyBlurEnabled',
  'privacyBlurMinutes',
  'privacyBlurMode',
  'exportPolicy',
  'alerts',
  'pdfWatermark'
]

export function isSchoolSystemAdmin(user) {
  return user?.role === 'school_admin' && user?.schoolSystemSettings === true
}

/**
 * School-scoped people follow their school's copy when one exists.
 * District office roles and role `admin` follow the district documents only —
 * role `admin` SAVES district documents, so letting it read a school's copy
 * would blend that copy into the district defaults on its next save.
 */
export function usesSchoolOverride(user) {
  if (!user?.role) return false
  return !['district_admin', 'district_coordinator', 'admin'].includes(user.role)
}

export function systemSettingsWriteTarget(user, schoolId) {
  if (isSchoolSystemAdmin(user)) {
    if (!schoolId) throw new Error('Choose a school before saving these settings.')
    return { scope: 'school', schoolId }
  }
  if (user?.role === 'admin' || user?.role === 'district_admin') {
    return { scope: 'district' }
  }
  throw new Error('You cannot change these settings.')
}

export function systemSettingsScopeNote(user, schoolName) {
  if (isSchoolSystemAdmin(user)) {
    return schoolName
      ? `These settings apply to ${schoolName} only. Other schools keep the district settings.`
      : 'Choose a school before saving. These settings apply to that school only.'
  }
  if (user?.role === 'admin' || user?.role === 'district_admin') {
    return 'These are the district defaults. A school that has saved its own copy uses that copy instead.'
  }
  return ''
}

export function overlaySecuritySettings(district, schoolSettings) {
  const base = district && typeof district === 'object' ? { ...district } : {}
  if (!schoolSettings || typeof schoolSettings !== 'object') return base
  for (const key of SECURITY_OVERRIDE_KEYS) {
    if (schoolSettings[key] !== undefined) base[key] = schoolSettings[key]
  }
  return base
}

export function applySchoolAdminPanelOverride(districtPermissions, schoolOverlay) {
  const out = { ...(districtPermissions || {}) }
  if (!schoolOverlay || typeof schoolOverlay !== 'object') return out
  for (const [page, grants] of Object.entries(schoolOverlay)) {
    if (SCHOOL_MATRIX_LOCKED_PAGES.includes(page)) continue
    if (!grants || typeof grants !== 'object') continue
    out[page] = { ...(out[page] || {}), ...grants }
  }
  return out
}

export function schoolAdminPanelPayload(permissions) {
  const out = {}
  for (const [page, grants] of Object.entries(permissions || {})) {
    if (SCHOOL_MATRIX_LOCKED_PAGES.includes(page)) continue
    out[page] = grants
  }
  return out
}
